import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { Project } from '../../models/project';
import { UserService } from '../../services/user.service';
import { UtilsService } from '../../services/utils.service';

@Component({
    selector: 'or-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ListComponent implements OnInit {

  @Input() public set items(data) {
    this._items = data;
    this.styleCache.clear(); // Clear style cache on data change

    if (this.layout != null) {
      this._items.forEach((row: any) => this.applyLayoutToRow(row));
    }

    // Pre-compute filtered actions for each item to avoid function calls in template
    if (this._items && this.actions) {
      this._items.forEach((row: any) => {
        row._filteredActions = this.filterRowActionPermissions(row);
      });
    }

    if (this.listsettings.sortBy != null) {
      this.sortList(this.listsettings.sortBy);
    }
    this.itemsToLoad = this.loadcount;
    this.updateDisplayList();
  }

  @Input() displayList: any;
  @Input() loadcount = 100;
  @Input() summary: any;
  @Input() showActionColLabel = false;

  @Input() public set layout(value: any[]) {
    this._layout = value;
    this.updateScrollbarMargin();
    // Pre-compute column label classes
    if (value) {
      value.forEach(col => {
        col._cssClass = `id-list-${col.label.toLowerCase().replace(/ /g, '')}`;
      });
    }
    if (value && this._items) {
      this._items.forEach((row: any) => this.applyLayoutToRow(row));
      this.updateDisplayList();
    }
  }
  public get layout(): any[] {
    return this._layout;
  }
  private _layout: any[];

  @Input() rowid = '';
  @Input() enableExport = true;
  @Input() showtooltip = false;
  @Input() tooltipMessage = '';
  @Input() clickroute = '';
  @Input() enableSelectedRowClass = false;
  @Input() exportFilename = 'export.csv';
  @Input() listsettings: any = { appliedFilters: [], sortBy: null };
  @Input() fetchFullNote: (row: any, colId: string) => Promise<string>; // Function to fetch full note content

  @Input() public set actions(value: any[]) {
    this._actions = value;
    // Re-filter actions for all items when actions input changes
    if (this._items && value) {
      this._items.forEach((row: any) => {
        row._filteredActions = this.filterRowActionPermissions(row);
      });
    }
  }
  public get actions(): any[] {
    return this._actions;
  }
  private _actions: any[];

  // Fixed columns settings
  @Input() public set fixedHeaderOnly(value: boolean) {
    if (this._fixedHeaderOnly !== value) {
      this._fixedHeaderOnly = value;
      this.styleCache.clear();
    }
  }
  public get fixedHeaderOnly(): boolean {
    return this._fixedHeaderOnly;
  }
  private _fixedHeaderOnly = false;

  @Input() public set fixedColumns(value: number) {
    if (this._fixedColumns !== value) {
      this._fixedColumns = value;
      this.styleCache.clear();
    }
  }
  public get fixedColumns(): number {
    return this._fixedColumns;
  }
  private _fixedColumns = 2;

  @Input() public set fixedColumnOffsets(value: any) {
    this._fixedColumnOffsets = value;
    this.styleCache.clear();
    this.updateScrollbarMargin();
  }
  public get fixedColumnOffsets(): any {
    return this._fixedColumnOffsets;
  }
  private _fixedColumnOffsets: any = [{left: 0, width: 140}, {left: 140, width: 140}]; // fix the first 2 cols by default and set widths

  @Output() selected = new EventEmitter();
  @Output() action = new EventEmitter();
  @Output() changed = new EventEmitter();
  @Output() settingsupdate = new EventEmitter();

  _items: any;
  displaylist: any;
  sortBy = '';
  sortDir = '';
  appliedFilters = [];
  itemsToLoad = 0;
  itemCount = 0;
  private styleCache = new Map<string, any>();
  scrollbarMarginLeft = '0px'; // Cache scrollbar margin calculation
  isInternal = false; // Cache auth.isInternal() to avoid method calls in template

  constructor(
    private userService: UserService,
    private utilService: UtilsService,
    public auth: AuthService,
    private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.isInternal = this.auth.isInternal(); // Cache auth check
    this.updateScrollbarMargin(); // Calculate initial scrollbar margin
    if (this.listsettings.sortBy != null) {
      this.sortList(this.listsettings.sortBy, this.listsettings.sortDir, true);
    }
    else this.updateDisplayList();
  }

  filterRowActionPermissions(project: any) {
    // Generic, opt-in per-row visibility: an action may define hidden(row) => boolean to hide itself
    // for specific rows (e.g. Delete only on certain statuses). Actions without it always show.
    let actions = this.actions.filter((a: any) => typeof a.hidden === 'function' ? !a.hidden(project) : true);
    const projectIsLive = (project.starts ?? 0) > 0 || project.projectStatus == 'Full Launch' || project.projectStatus == 'Soft Launch'; ;
    if(!this.auth.isSuperAdmin() && projectIsLive) {
      return actions.filter(e => e.type !== 'delete');
    }
    return actions;
  }

  private applyLayoutToRow(row: any) {
    this.layout.forEach((col: any) => {
      if (col.editOnly) {
        row.editMode = row.editMode ?? {};
        row.editMode[col.id] = { enabled: true, value: row[col.id] };
      }
    });
    this.preComputeRowValues(row);
  }

  preComputeRowValues(row: any) {
    // Pre-compute expensive values and store them on the row object
    if (!this.layout) return;

    this.layout.forEach((col: any) => {
      const colId = col.id;
      const value = row[colId];

      // Pre-compute cleanID implementation and store key
      if (col.type === 'cleanID') {
        const key = `_computed_${colId}`;
        row[key] = this.cleanIDImplementation(row, col);
        // Store the key itself to avoid concatenation in template
        if (!col._computedKey) col._computedKey = key;
      }

      // Pre-compute countdown string
      if (col.type === 'countdown' && value != null) {
        const key = `_computed_countdown_${colId}`;
        row[key] = this.getCountdownString(value);
        if (!col._computedCountdownKey) col._computedCountdownKey = key;
      }

      // Pre-compute date string for tooltips
      if (col.type === 'datetimeToolTip' && value != null) {
        const key = `_computed_date_${colId}`;
        row[key] = this.getLocaleDateString(value);
        if (!col._computedDateKey) col._computedDateKey = key;
      }

      // Pre-compute notes
      if (col.type === 'notes' && value) {
        const key = `_computed_note_${colId}`;
        row[key] = this.getNote(value);
        if (!col._computedNoteKey) col._computedNoteKey = key;
      }

      // Pre-compute percent complete
      if (col.type === 'stage') {
        row._computed_percentComplete = (row?.stageCompleteTaskCount / row?.stageTotalTaskCount) * 100;
      }

      // Pre-compute isArrayOfObjects for users type
      if (col.type === 'users' && value != null) {
        row[`_isArray_${colId}`] = Array.isArray(value) && typeof value[0] === 'object';
      }

      // Pre-compute visible/overflow country codes
      if (col.type === 'countries' && value != null) {
        const maxVisible = col.maxVisible ?? 3;
        const visibleLabelKey = `_computed_countriesVisibleLabel_${colId}`;
        const hiddenLabelKey = `_computed_countriesHiddenLabel_${colId}`;
        const hiddenCountKey = `_computed_countriesHiddenCount_${colId}`;
        const visible = this.utilService.getVisibleCountries(value, maxVisible);
        const hidden = this.utilService.getHiddenCountries(value, maxVisible);

        row[visibleLabelKey] = visible.map(c => c.code).join(', ');
        row[hiddenLabelKey] = hidden.map(c => c.code).join(', ');
        row[hiddenCountKey] = hidden.length;

        if (!col._computedVisibleLabelKey) col._computedVisibleLabelKey = visibleLabelKey;
        if (!col._computedHiddenLabelKey) col._computedHiddenLabelKey = hiddenLabelKey;
        if (!col._computedHiddenCountKey) col._computedHiddenCountKey = hiddenCountKey;
      }

      // Pre-compute tooltip display logic
      if (value != null && typeof value === 'string') {
        row[`_showTooltip_${colId}`] = value.length * 8 > col.width;
        row[`_truncated_${colId}`] = value.length > 12 ? value.slice(0, 12) + '...' : value;
      }
    });
  }

  updateDisplayList() {
    if (this._items === undefined || this._items === null) return;
    const items = this._items.filter(row => this.shouldShowRow(row))
    this.itemCount = items.length;
    this.displayList = items.splice(0, this.itemsToLoad);
    this.cdr.markForCheck(); // Trigger change detection with OnPush strategy
  }
  loadMore() {
    this.itemsToLoad += this.loadcount;
    this.updateDisplayList();
  }
  // this function is decalred differently as it is passed to the child component and called from there
  getUserList = (filter) => {
    return this.userService.GetUsers();
  }
  sortList(col: any, order: string = 'ASC', force: boolean = false) {
    // Local Sort: for server side sort we will need to emit event so that calling component can fetch new data
    const numberToShow = this._items.length;

    if (this.listsettings.sortBy !== null && this.listsettings.sortBy.id === col.id && !force) {
      this.listsettings.sortDir = (this.listsettings.sortDir === 'ASC') ? 'DESC' : 'ASC';
      this._items = this._items.reverse();
    } else {
      this.listsettings.sortBy = col;
      this.listsettings.sortDir = order;

      let sortFn = (a, b) => (a[col.id] || '').localeCompare(b[col.id] || '');
      if (col.type === 'number' || col.type === 'alert' || col.type === 'currency' || col.type === 'percent') {
        sortFn = (a, b) => Number(a[col.id]) - Number(b[col.id]);
      } else if (col.type === 'users') {
        sortFn = (a, b) => (a[col.id] == null || a[col.id].length == 0 ? '' : a[col.id][0].name ?? '')
          .localeCompare(b[col.id] == null || b[col.id].length == 0 ? '' : b[col.id][0].name ?? '');
      }

      this._items = this._items.sort(sortFn);
      if (order == 'DESC')
        this._items = this._items.reverse();
    }
    this.styleCache.clear(); // Clear cache when sort changes row positions
    this.updateDisplayList();
    this.settingsUpdated();
  }

  settingsUpdated() {
    this.settingsupdate.emit(this.listsettings);
  }

  getFilterValues(col) {
    // TO DO : GO TO SERVER IF INFO NOT AVAILABLE IN LOCAL VALUES (SIMILAR FOR SORT)
    let values = {};
    if (col.type === 'users') {
      this._items.forEach(x => {
        if (x[col.id]?.length > 0) {
          for (var i = 0; i < x[col.id]?.length; i++) {
            values[x[col.id][i].name] = (typeof values[x[col.id][i].name] === 'undefined') ? 1 : values[x[col.id][i].name] + 1;
          }
        }
      });
    } else if (col.type === 'tags') {
      this._items.forEach(x => {
        x[col.id].forEach(tag => {
          values[tag.tag] = (typeof values[tag.tag] === 'undefined') ? 1 : values[tag.tag] + 1;
        });
      });
    } else {
      values = this._items.map(x => x[col.id]).reduce((acc, curr) => {
        acc[curr] = (typeof acc[curr] === 'undefined') ? 1 : acc[curr] + 1;
        return acc;
      }, {});
    }

    const filterValues = [];
    const filterDetails = this.listsettings.appliedFilters.filter(e => e.id === col.id);

    for (const val in values) {
      let isSelected = (filterDetails.length > 0 && filterDetails[0].values.includes(val));
      filterValues.push({ label: val, count: values[val], selected: isSelected });
    }
    col.filterValues = filterValues.sort((a, b) => (a.label > b.label) ? 1 : -1);

    this.updateDisplayList();
  }

  applyFilter(values, col) {
    const curFilters = this.listsettings.appliedFilters.filter(e => e.id !== col.id);
    if (values.length > 0) {
      if (col.type === 'bool') {
        curFilters.push({ id: col.id, label: col.label, values: values.map(e => e.label === 'true') });
      } else {
        curFilters.push({ id: col.id, type: col.type, label: col.label, values: values.map(e => e.label) });
      }
    }
    this.listsettings.appliedFilters = curFilters;
    this.styleCache.clear(); // Clear cache when filter changes visible rows
    this.settingsUpdated();
    this.updateDisplayList();
  }

  removeFilter(col) {
    this.listsettings.appliedFilters = this.listsettings.appliedFilters.filter(e => e.id !== col.id);
    this.styleCache.clear(); // Clear cache when filter changes visible rows
    this.settingsUpdated();
    this.updateDisplayList();
  }
  private escapeCSVValue(value: any): string {
    if (value == null || value === undefined) {
      return '';
    }

    // Convert to string and strip HTML tags
    let stringValue = String(value).replace(/<[^>]*>/g, '');

    // Normalize special characters to prevent encoding issues
    stringValue = stringValue
      .replace(/[\u2018\u2019]/g, "'")  // Smart single quotes to apostrophe
      .replace(/[\u201C\u201D]/g, '"')  // Smart double quotes to straight quotes
      .replace(/[\u2013\u2014]/g, '-')  // En dash and em dash to hyphen
      .replace(/\u2026/g, '...')        // Ellipsis to three dots
      .replace(/[\u00A0]/g, ' ')        // Non-breaking space to regular space
      .replace(/&nbsp;/g, ' ')          // HTML non-breaking space
      .replace(/&amp;/g, '&')           // HTML ampersand
      .replace(/&lt;/g, '<')            // HTML less than
      .replace(/&gt;/g, '>')            // HTML greater than
      .replace(/&quot;/g, '"')          // HTML quote
      .replace(/&#39;/g, "'")           // HTML apostrophe
      .replace(/[^\x20-\x7E\t\n\r]/g, (char) => {
        // Replace other non-ASCII characters with their closest ASCII equivalent or remove them
        const code = char.charCodeAt(0);
        if (code < 128) return char;
        return char.normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
      })
      .trim(); // Remove leading/trailing whitespace

    // If value contains comma, newline, or double quote, wrap in quotes and escape internal quotes
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"') || stringValue.includes('\r')) {
      stringValue = stringValue.replace(/"/g, '""'); // Escape quotes by doubling them
      return `"${stringValue}"`;
    }

    return stringValue;
  }

  async exportTable() {
    try {
      // Add UTF-8 BOM to help Excel recognize encoding
      let csvContent = '\uFEFF';

      // Column headers
      const headerColumns = this.layout.filter(col => 
        col.type !== 'img' && 
        col.type !== 'actions-inline' && 
        col.type !== 'actions-dropdown' && 
        col.type !== 'actions-checkbox'
      );

      csvContent += headerColumns.map(col => this.escapeCSVValue(col.label)).join(',') + '\n';

      // Data rows
      const items = this._items?.filter(row => this.shouldShowRow(row)) || [];
      
      for (const row of items) {
        const rowValues: string[] = [];

        for (const col of this.layout) {
          // Skip non-exportable column types
          if (col.type === 'img' || col.type === 'actions-inline' || 
              col.type === 'actions-dropdown' || col.type === 'actions-checkbox') {
            continue;
          }

          let cellValue = '';

          try {
            if (row[col.id] == null) {
              cellValue = '';
            } else if (col.type === 'tag') {
              cellValue = this.escapeCSVValue(row[col.id]?.label);
            } else if (col.type === 'address') {
              cellValue = this.escapeCSVValue(row[col.id]?.address1);
            } else if (col.type === 'text') {
              cellValue = this.escapeCSVValue(row[col.id]);
            } else if (col.type === 'users') {
              const userName = row[col.id]?.length > 0 && row[col.id][0]?.name 
                ? row[col.id][0].name 
                : '';
              cellValue = this.escapeCSVValue(userName);
            } else if (col.type === 'tags') {
              const tags = row[col.id]?.map((tag: any) => tag?.tag).filter((t: any) => t).join('|') || '';
              cellValue = this.escapeCSVValue(tags);
            } else if (col.type === 'cleanID') {
              cellValue = this.escapeCSVValue(this.cleanIDImplementation(row, col));
            } else if (col.type === 'notes') {
              // Fetch full note content if fetch function is provided and note exists
              if (row[col.id] != null && this.fetchFullNote) {
                try {
                  const fullNote = await this.fetchFullNote(row, col.id);
                  cellValue = this.escapeCSVValue(fullNote);
                } catch (error) {
                  console.error(`Error fetching full note for row:`, error);
                  cellValue = this.escapeCSVValue(row[col.id]); // Fallback to truncated
                }
              } else {
                cellValue = this.escapeCSVValue(row[col.id]);
              }
            } else if (col.type === 'date' || col.type === 'countdown' || col.type === 'datetimeToolTip') {
              const dateValue = row[col.id] ? new Date(row[col.id]).toLocaleDateString() : '';
              cellValue = this.escapeCSVValue(dateValue);
            } else if (col.type === 'number' || col.type === 'currency' || col.type === 'percent') {
              cellValue = row[col.id] != null ? (col.prefix || '') + String(row[col.id]) + (col.suffix || '') : '';
            } else if (col.type === 'bool') {
              cellValue = row[col.id] != null ? String(row[col.id]) : '';
            } else {
              cellValue = this.escapeCSVValue(row[col.id]);
            }
          } catch (error) {
            console.error(`Error processing column ${col.id}:`, error);
            cellValue = '';
          }

          rowValues.push(cellValue);
        }

        csvContent += rowValues.join(',') + '\n';
      }

      this.utilService.generateXLSX(csvContent, this.exportFilename);
    } catch (error) {
      console.error('Error exporting table:', error);
    }
  }

  getCountdownString(inDate) {
    if (inDate == null) { return ''; }
    const msPerDay = 1000 * 3600 * 24; // transform milliseconds to total of days (milliseconds * seconds * hours)
    const theDate = new Date(inDate).valueOf();
    const totalDaysLeft = Math.ceil((theDate - new Date().valueOf()) / msPerDay);
    return totalDaysLeft < 0 ? 'No Days Left' : totalDaysLeft === 1 ? '1 Day Left' : totalDaysLeft + ' Days Left';
  }
  getLocaleDateString(inDate) {
    return this.formatDateIfValid(inDate)
  }
  shouldShowRow(row) {
    // MIGHT BE BETTER WAY TO DO THIS AND USE A FILTERED LIST WHICH UPDATES THE DATA
    if (this.listsettings != null && this.listsettings.appliedFilters && this.listsettings.appliedFilters.length === 0) { return true; }

    let found = true;

    for (let idx = 0; idx < this.listsettings.appliedFilters.length && found; idx++) {
      const filter = this.listsettings.appliedFilters[idx];
      if (filter.type === 'users') {
        if (row[filter.id] == null || row[filter.id].length == 0) { return false; }

        for (const user of row[filter.id]) {
          found = filter.values.includes(user.name);
          if (found)
            break;
        }
      } else if (filter.type === 'tags') {
        const matches = row[filter.id].find(tag => filter.values.includes(tag.tag));
        found = (matches != null);
      } else {
        found = filter?.values?.includes(row[filter.id]);
      }
    }
    return found;
  }

  rowSelected(col, row) {
    if (row.fileName != 'DecipherAPI' && row.fileName != 'ConfirmitAPI') {
      if (col.selectable) {
        //add class "table-info" to highlight the selected row
        if (this.enableSelectedRowClass)
          row.selected = true;
        this.selected.emit(row);
      }
    }
  }

  enableEdit(item, col) {
    let val = item[col.id];

    if (item.editMode == null) { item.editMode = {}; }

    if (col.type === 'date' || col.type === 'countdown') {
      val = (item[col.id] === null) ? new Date() : new Date(item[col.id]);
    }

    item.editMode[col.id] = { enabled: true, value: val };
  }

  autoSaveChange(newVal, item, col) {
    item.editMode[col.id].value = newVal;
    if (col.autoSave) {
      if (col.type === 'date' || col.type === 'countdown') {
        if (new Date(item[col.id]).getTime() !== newVal.getTime()) {
          this.saveChange(item, col);
        }
      } else if (item[col.id] !== newVal) {
        this.saveChange(item, col);
      }
    }
  }

  saveChange(item, col) {
    const newValue = item.editMode[col.id].value;
    item[col.id] = newValue; // update locally while we wait to save to DB, TO DO: MAYBE SHOW SMALL LOADER
    if (col.editOnly !== true) {
      item.editMode[col.id] = null;
    }
    this.changed.emit({ item, col, newValue });
  }

  onStatusChanged(event: { status: string }, item: any, col: any): void {
    item[col.id] = event.status;
    this.changed.emit({ item, col, newValue: event.status });
    this.cdr.markForCheck();
  }

  actionSelected(row, type, evt = null) {
    this.action.emit({ row, type, evt });
  }

  getNote(content: string): string {
    if (!content) return '';

    const textOnly = content.replace(/<[^>]*>/g, '');

    const maxChars = 100;

    if (textOnly.length <= maxChars) {
      return content;
    }

    const truncated = content.substring(0, maxChars);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    const breakPoint = lastSpaceIndex > maxChars * 0.8 ? lastSpaceIndex : maxChars;
    return content.substring(0, breakPoint) + ' . . .';
  }

  clientStatusSelected(item, col, newValue, ddl) {
    item[col.id] = newValue;
    this.changed.emit({ item, col, newValue });
    ddl?.toggle(false);
  }

  onWindowScroll(evt: Event) {
    // const verticalOffset = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    // const tableOffsetTop = evt.target.offset().top;
  }

  isArrayOfObjects(value: any): boolean {
    return Array.isArray(value) && typeof value[0] === 'object';
  }

  cleanIDImplementation(item, col): string {
    if (item.cleanIDEnable == false) {
      return "Disabled"
    } else if (item.cleanIDEnable == true && item.cleanIDImplementation == null) {
      return "Enabled"
    }
    return item[col.id]
  }
  formatDateIfValid(dateStr: string): string {
    if (
      !dateStr ||
      ['1/1/01', '001', '0001', '0000-00-00', '0001-01-01T00:00:00'].includes(dateStr)
    ) return '';

    try {

      const date = new Date(dateStr);
      // Get UTC parts to avoid local time conversion
      const month = date.getUTCMonth() + 1;
      const day = date.getUTCDate();
      const year = date.getUTCFullYear().toString().slice(-2);
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      const timezone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(date)
        .find(part => part.type === 'timeZoneName')?.value || '';
      // Format 12-hour time with AM/PM
      const isPM = hours >= 12;
      const hour12 = hours % 12 === 0 ? 12 : hours % 12;
      const period = isPM ? 'PM' : 'AM';
      const formatted = `${month}/${day}/${year} @ ${hour12}:${minutes} ${period} ${timezone}`;
      return formatted;

    } catch (error) {
      return '';
    }
  }

  getStickyStyle(rowIndex: number, colIndex: number, header: boolean): any {
    // Use memoization to cache computed styles
    const cacheKey = `${rowIndex}-${colIndex}-${header}`;

    if (this.styleCache.has(cacheKey)) {
      return this.styleCache.get(cacheKey);
    }

    const isStriped = rowIndex == -1 ? false : (rowIndex % 2 !== 0);
    const colOffset = this.fixedColumnOffsets[colIndex] || null;
    const isLastFixed = colIndex === this.fixedColumns - 1;
    const boxShadow = 'inset -2px 0 2px -2px rgba(0,0,0,0.10)';
    const isFirstColActions = (this.layout[0].type === 'actions' || this.layout[0].type === 'actions-dropdown' || this.layout[0].type === 'actions-inline');
    const isActionsColumn = (this.layout[colIndex]?.type === 'actions' || this.layout[colIndex]?.type === 'actions-dropdown' || this.layout[colIndex]?.type === 'actions-inline');
    let bgColor = isStriped ? '#fdfdfe' : '#f0f0f1';

    // fix only the header row
    if (this.fixedHeaderOnly) {
      if (header) {
        const style: any = {
          position: 'sticky',
          top: '-1px',
          zIndex: 5,
          background: '#fdfdfe',
        };

        if (isActionsColumn) {
          style.width = '70px';
          style.minWidth = '70px';
        }

        this.styleCache.set(cacheKey, style);
        return style;
      } else {
        const style: any = {
          background: bgColor
        };

        if (isActionsColumn) {
          style.width = '70px';
          style.minWidth = '70px';
        }

        this.styleCache.set(cacheKey, style);
        return style;
      }
    }

    if (header) {

      bgColor = isStriped ? '#f0f0f1' : '#fdfdfe';
      const style: any = {
        position: 'sticky',
        top: '-1px',
        background: bgColor,
        zIndex: colIndex < 2 ? 5 : 3
      };

      if (colOffset) {
        // If this is column 1 and first column is actions type, left should be 70
        let left = colOffset.left;
        if (colIndex === 1 && isFirstColActions) {
          left = 55;
        }
        style.left = `${left}px`;

        const width = isActionsColumn ? 55 : colOffset.width;
        style.width = `${width}px`;
        style.minWidth = `${width}px`;
      }

      if (isLastFixed) {
        style.boxShadow = boxShadow;
      }

      this.styleCache.set(cacheKey, style);
      return style;
    }


    // fix columns from left
    if (colIndex >= this.fixedColumns) {
      const style: any = {
        position: 'relative',
        zIndex: colIndex < 2 ? 3 : 0,
        background: bgColor
      };

      if (isActionsColumn) {
        style.width = '70px';
        style.minWidth = '70px';
      }

      this.styleCache.set(cacheKey, style);
      return style;
    }

    // If this is column 1 and first column is actions type, left should be 70
    let left = colOffset?.left;
    if (colIndex === 1 && isFirstColActions) {
      left = 55;
    }

    const width = isActionsColumn ? 55 : colOffset?.width;

    const style: any = {
      position: 'sticky',
      left: `${left}px`,
      width: `${width}px`,
      minWidth: `${width}px`,
      zIndex: colIndex < 2 ? 3 : 0,
      background: bgColor
    };

    if (isLastFixed) {
      style.boxShadow = boxShadow;
    }

    this.styleCache.set(cacheKey, style);
    return style;
  }

  updateScrollbarMargin(): void {
    if (!this.layout || !this.fixedColumnOffsets) return;

    const isFirstColActions = (this.layout[0]?.type === 'actions' || this.layout[0]?.type === 'actions-dropdown' || this.layout[0]?.type === 'actions-inline');
    let totalFixedWidth = this.fixedColumnOffsets.slice(0, this.fixedColumns).reduce((sum: number, col: any) => sum + (col?.width || 0), 0);

    // If first column is actions, subtract its offset width and add 55px instead
    if (isFirstColActions && this.fixedColumnOffsets[0]) {
      totalFixedWidth = totalFixedWidth - this.fixedColumnOffsets[0].width + 55;
    }

    const marginLeft = totalFixedWidth - 1;
    this.scrollbarMarginLeft = `${marginLeft}px`;
  }

  getScrollbarMarginLeft(): string {
    return this.scrollbarMarginLeft;
  }

}
